<template>
  <div class="zone-total unique-total widget__content-center widget__content-center--flex">
    <div class="widget__label">
      Total
      <span class="zone-total__count">{{ formatNumber(grandTotal, 0) }}</span>
    </div>

    <div v-if="chartSeries.length" class="chart-wrapper">
      <apexchart
        width="100%"
        height="100%"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { barColors } from '@/utils/chartColors';
import type { DataUniqueVisitors } from '@/types/Widget';
import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';
const props = defineProps<{
  widget: DataUniqueVisitors;
}>();

const chartDataFormatted = computed(() => {
  const dataZones = props.widget.data['Zone'];

  return Object.keys(dataZones).map((key) => {
    const areas = props.widget.filters.area;
    const zone = areas.find((area) => area.name === 'Zone');
    const zoneData = zone?.data;

    const zoneName = zoneData?.find((zoneFilter) => zoneFilter.id === key)?.title;

    const newZone = {
      id: key,
      name: zoneName,
      data: [
        dataZones[key]['Total']['Engaged Visitors'],
        dataZones[key]['Total']['Passerby Visitors'],
      ],
    };
    return newZone;
  });
});

const chartSeries = computed(() => {
  type SeriesData = {
    x: string;
    y: number;
  };

  const engagedData: SeriesData[] = [];
  const passerData: SeriesData[] = [];

  chartDataFormatted.value.forEach((char) => {
    engagedData.push({
      x: 'Engaged',
      y: char.data[0],
    });
    passerData.push({
      x: 'Passerby',
      y: char.data[1],
    });
  });

  return [
    {
      name: 'Engaged',
      data: engagedData,
    },
    {
      name: 'Passerby',
      data: passerData,
    },
  ];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = computed<BaseChartOptions>(() => ({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'bar',
    stacked: true,
    id: 'meshh-chart-zone-total',
    toolbar: getChartExportSettings(props.widget.name, 'Zone', 'Total', '', '', ''),
  },
  plotOptions: {
    bar: {
      horizontal: true,
    },
  },
  colors: barColors,
  xaxis: {
    categories: chartDataFormatted.value.map((chart) => chart.name),
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 0);
      },
    },
  },
}));

const grandTotal = computed(() => {
  const usableData = props.widget.data['Site']['Total'];
  const engagedVisitors = usableData['Engaged Visitors'];
  const passerbyVisitors = usableData['Passerby Visitors'];
  return engagedVisitors + passerbyVisitors;
});
</script>

<style lang="scss">
.widget .zone-total {
  .widget__label {
    justify-content: flex-start;
    padding: 0;
  }

  .zone-total__count {
    @include typography.font-display($size: sm, $weight: bold);
    @include mixins.rounded-border();
    @include sizing.padding('xxxs', 'xxs');
    background: variables.$primitives-color-border-100;
    margin-left: variables.$sizing-xxs;
  }
}
</style>
